import { Star } from 'lucide-react';
import { useDemo } from '@app/components/demo/DemoProvider';
import { BaseForecastModelWithAvailability } from '@app/common/static-data/models';
import { ModelColorButton } from './ModelColorButton';
import { InitTimeSelector } from './InitTimeSelector';

interface ModelCardProps {
  model: BaseForecastModelWithAvailability;
  isSelected: boolean;
  isFavorite: boolean;
  onToggle: () => void;
  onToggleFavorite: () => void;
  disabled: boolean;
  modelColor: string;
  onColorChange: (modelId: string, color: string) => void;
  availableInitTimes?: string[];
  loadingInitTimes?: Record<string, boolean>;
  openInitTimeSelectors?: Record<string, boolean>;
  selectedInitTime?: string;
  onInitTimeChange?: (modelId: string, initTime: string) => void;
  onInitTimeSelectorToggle?: (modelId: string) => void;
}

export function ModelCard({
  model,
  isSelected,
  isFavorite,
  onToggle,
  onToggleFavorite,
  disabled,
  modelColor,
  onColorChange,
  availableInitTimes,
  loadingInitTimes,
  openInitTimeSelectors,
  selectedInitTime = 'live',
  onInitTimeChange,
  onInitTimeSelectorToggle,
}: ModelCardProps) {
  const showInitTimeSelector =
    model.provider === 'jua' || model.id === 'ecmwf_ifs_single' || model.id === 'noaa_gfs_single';
  const { isDemo } = useDemo();

  return (
    <div
      className={`
        relative p-3 rounded-lg border cursor-pointer transition-all duration-200 group
        ${
          isSelected
            ? 'border-pink-300 bg-pink-50 dark:border-pink-500 dark:bg-pink-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }
        ${!model.available ? 'cursor-not-allowed' : 'hover:shadow-sm'}
      `}
      onClick={!disabled && model.available ? onToggle : undefined}
    >
      {!model.available && (
        <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
          {isDemo ? 'Subscribe for Access' : 'Available in Pro'}
        </span>
      )}

      <div className={disabled || !model.available ? 'opacity-50' : ''}>
        <button
          className={`
            absolute top-2 right-2 p-1 rounded transition-colors
            ${isFavorite ? 'text-pink-500' : 'text-gray-400 hover:text-pink-500'}
          `}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled && model.available) onToggleFavorite();
          }}
        >
          <Star className={`h-3 w-3 ${isFavorite ? 'fill-current' : ''}`} />
        </button>

        <div className="pr-6">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{model.label}</h4>
            <ModelColorButton
              modelId={model.id}
              currentColor={modelColor}
              onColorChange={onColorChange}
              disabled={disabled || !model.available}
            />
          </div>
          {model.banner && (
            <span className="inline-block px-2 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded mb-1.5">
              {model.banner}
            </span>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-1">
            {model.description}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>{model.resolution}</span>
            <span>•</span>
            <span>{model.updateFrequency}</span>
          </div>

          {/* Init Time Selector */}
          {showInitTimeSelector && onInitTimeChange && (
            <div onClick={(e) => e.stopPropagation()}>
              <InitTimeSelector
                availableInitTimes={availableInitTimes}
                disabled={disabled}
                isOpen={openInitTimeSelectors?.[model.id] || false}
                loading={loadingInitTimes?.[model.id] || false}
                selectedInitTime={selectedInitTime}
                onToggle={() => onInitTimeSelectorToggle?.(model.id)}
                onSelect={(initTime) => onInitTimeChange?.(model.id, initTime)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
